import React, { useState } from 'react';
import { ref, push } from 'firebase/database';
import { db } from '../firebase';
import { ContactMessage } from '../types';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const HERO_IMG = 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=1800&q=85&fit=crop';

const Contact: React.FC = () => {
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data: ContactMessage = { ...form, createdAt: Date.now(), read: false };
      await push(ref(db, 'contacts'), data);
      setDone(true);
    } catch {
      setError('An error occurred. Please contact us directly at info@uaa.education.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="form-page">
        <div
          className="form-hero"
          style={{
            backgroundImage: `linear-gradient(160deg, rgba(4,15,30,0.88) 0%, rgba(11,37,69,0.82) 100%), url(${HERO_IMG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <span style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', color: 'rgba(184,150,46,0.8)', display: 'block', marginBottom: 12 }}>Contact</span>
          <h1>Get in Touch</h1>
          <p>Our team is available to assist with admissions inquiries, program information, and general questions.</p>
        </div>
        <div className="form-body" style={{ maxWidth: 720 }}>
          {done ? (
            <div className="success-msg">
              <div className="success-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <h2>Message Received</h2>
              <p>Thank you for contacting the University of American Atlantic. A member of our team will respond to your inquiry within 1–2 business days.</p>
            </div>
          ) : (
            <>
              {/* Contact Info */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: 'var(--border)', marginBottom: 36, border: '1px solid var(--border)' }}>
                {[
                  { label: 'Address', value: 'Pinellas County, Florida, USA' },
                  { label: 'Telephone', value: '+1 (404) 859-5563' },
                  { label: 'Email', value: 'info@uaa.education' },
                ].map(c => (
                  <div key={c.label} style={{ background: '#fff', padding: '18px 20px', textAlign: 'center' }}>
                    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.68rem', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 6 }}>{c.label}</div>
                    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.84rem', color: 'var(--text-light)' }}>{c.value}</div>
                  </div>
                ))}
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-section-title">Contact Information</div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input name="fullName" required value={form.fullName} onChange={handleChange} placeholder="Your full name" />
                  </div>
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input name="email" type="email" required value={form.email} onChange={handleChange} placeholder="your@email.com" />
                  </div>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input name="phone" value={form.phone} onChange={handleChange} placeholder="+1 (555) 000-0000" />
                  </div>
                  <div className="form-group">
                    <label>Subject *</label>
                    <select name="subject" required value={form.subject} onChange={handleChange}>
                      <option value="">Select a subject</option>
                      <option>Admissions Inquiry</option>
                      <option>Program Information</option>
                      <option>Tuition &amp; Fees</option>
                      <option>RPL / Credit Transfer</option>
                      <option>Technical Support</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="form-group full">
                    <label>Message *</label>
                    <textarea name="message" required value={form.message} onChange={handleChange} placeholder="Please provide as much detail as possible to help us respond accurately." style={{ minHeight: 130 }} />
                  </div>
                </div>
                {error && <p style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: '#dc2626', fontSize: '0.85rem', marginTop: 12 }}>{error}</p>}
                <div className="form-submit">
                  <button className="btn-submit" type="submit" disabled={loading}>
                    {loading ? 'Sending Message...' : 'Send Message'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Contact;
